import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "notistack";
import { DateTime } from "luxon";

import { useWidget } from "/imports/state/WidgetContext";
import { useAuth } from "/imports/state/AuthContext";
import { useBackTo } from "/imports/ui/components/discovery";
import { useUrlFilters } from "/imports/libs/url";
import api from "/imports/api/client";
import useFetch from "/imports/hooks/useFetch";

// Events run a handful of dates a year, so the page lists a whole year ahead
// in one go instead of paging week by week.
const HORIZON_MONTHS = 12;

const optionsFrom = (events, idOf, labelOf) => {
  const labels = new Map();
  for (const event of events) {
    const id = idOf(event);
    if (id && !labels.has(id)) labels.set(id, labelOf(event));
  }
  return Array.from(labels.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export default function useEvents() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { categoryId } = useParams();
  const { slug, services, selectedLocation, currency, addToCart } = useWidget();
  const { isAuthenticated, activeClientId } = useAuth();
  const goBack = useBackTo(`/${slug}`);
  const { filters, setFilter } = useUrlFilters();

  const categoryName = useMemo(() => (services || []).find((s) => s.categoryId === categoryId)?.categoryName || "Events", [services, categoryId]);

  const widgetLocationId = selectedLocation?._id;

  const { data, loading, error } = useFetch(
    () =>
      api.getClassTimetable(slug, {
        categoryId,
        dateStart: DateTime.now().toISODate(),
        dateEnd: DateTime.now().plus({ months: HORIZON_MONTHS }).toISODate(),
        locationId: widgetLocationId
      }),
    [slug, categoryId, widgetLocationId, activeClientId],
    { skip: !slug || !categoryId }
  );

  const allEvents = useMemo(() => [...(data?.classes || [])].sort((a, b) => a.startDate.localeCompare(b.startDate)), [data]);
  const myLevels = data?.filters?.myLevels || [];
  const levelNames = data?.filters?.levels || [];

  // Filters carried in a shared link that no longer match anything fall back to
  // everything rather than an empty page.
  const allServiceIds = new Set(allEvents.map((e) => e.serviceId));
  const allLocationIds = new Set(allEvents.map((e) => e.location?._id).filter(Boolean));
  const allLevelValues = new Set(allEvents.flatMap((e) => e.levelRequired || []).map(String));
  const selectedServiceId = allServiceIds.has(filters.serviceId) ? filters.serviceId : null;
  const selectedLocationId = allLocationIds.has(filters.locationId) ? filters.locationId : null;

  // "My level" is the events this client can actually join: their level sits in
  // the event's range, or the event has no level gate. It needs an assessed
  // client, and an assessed client opens on it — "all" in the URL is the
  // explicit opt-out, so it survives opening an event and coming back.
  const canUseMyLevel = isAuthenticated && myLevels.length > 0;
  const levelScope = filters.level === "all" ? "all" : allLevelValues.has(filters.level) ? filters.level : canUseMyLevel ? "mine" : "all";

  const matchesService = (e) => !selectedServiceId || e.serviceId === selectedServiceId;
  const matchesLocation = (e) => !selectedLocationId || e.location?._id === selectedLocationId;
  const matchesLevel = (e) => {
    if (levelScope === "all") return true;
    if (levelScope === "mine") return e.eligibility?.eligible !== false;
    const required = (e.levelRequired || []).map(String);
    return required.length === 0 || required.includes(levelScope);
  };

  // Each filter only offers what still has dates under the other two, so no
  // combination of picks can leave the page empty.
  const serviceOptions = useMemo(
    () =>
      optionsFrom(
        allEvents.filter((e) => matchesLocation(e) && matchesLevel(e)),
        (e) => e.serviceId,
        (e) => e.name
      ),
    [allEvents, selectedLocationId, levelScope]
  );
  const locationOptions = useMemo(
    () =>
      optionsFrom(
        allEvents.filter((e) => matchesService(e) && matchesLevel(e)),
        (e) => e.location?._id,
        (e) => e.location?.name || ""
      ),
    [allEvents, selectedServiceId, levelScope]
  );
  const levelOptions = useMemo(() => {
    const present = new Set(
      allEvents
        .filter((e) => matchesService(e) && matchesLocation(e))
        .flatMap((e) => e.levelRequired || [])
        .map(String)
    );
    const specific = [...present]
      .sort((a, b) => Number(a) - Number(b))
      .map((value) => {
        const name = levelNames.find((l) => String(l.value) === value)?.label;
        return { value, label: name && !name.startsWith("Level") ? `Level ${value} · ${name}` : `Level ${value}` };
      });
    const myLabel = myLevels.map((l) => (myLevels.length > 1 ? `${l.sportName} ${l.levelNames.join(" + ")}` : l.levelNames.join(" + "))).join(" / ");
    const heldCount = myLevels.reduce((n, l) => n + l.levelNames.length, 0);
    return [...(canUseMyLevel ? [{ value: "mine", label: heldCount > 1 ? "My levels" : "My level", sublabel: myLabel }] : []), ...specific];
  }, [allEvents, selectedServiceId, selectedLocationId, levelNames, myLevels, canUseMyLevel]);

  const events = useMemo(
    () => allEvents.filter((e) => matchesService(e) && matchesLocation(e) && matchesLevel(e)),
    [allEvents, selectedServiceId, selectedLocationId, levelScope]
  );

  // Events the level choice alone is holding back — an assessed client lands on
  // their own level, so an empty page may just be events for other levels.
  const hiddenByLevel = levelScope === "all" ? 0 : allEvents.filter((e) => matchesService(e) && matchesLocation(e) && !matchesLevel(e)).length;

  // The picker offers only days that actually have an event on them.
  const eventDates = useMemo(() => {
    const counts = new Map();
    for (const event of events) counts.set(event.date, (counts.get(event.date) || 0) + 1);
    return Array.from(counts.entries()).map(([date, count]) => ({ date, count }));
  }, [events]);

  const selectedDate = filters.date && eventDates.some((d) => d.date === filters.date) ? filters.date : null;

  const eventsByMonth = useMemo(() => {
    const groups = new Map();
    for (const event of events) {
      if (selectedDate && event.date !== selectedDate) continue;
      const month = event.date.slice(0, 7);
      if (!groups.has(month)) groups.set(month, []);
      groups.get(month).push(event);
    }
    return Array.from(groups.entries()).map(([month, items]) => ({
      month,
      label: DateTime.fromISO(`${month}-01`).toFormat("LLLL yyyy"),
      items
    }));
  }, [events, selectedDate]);

  const filterDefs = [
    {
      key: "event",
      label: "Event",
      allLabel: "All events",
      options: serviceOptions,
      value: selectedServiceId,
      onChange: (v) => setFilter("serviceId", v)
    },
    {
      key: "location",
      label: "Location",
      allLabel: "All locations",
      options: locationOptions,
      value: selectedLocationId,
      onChange: (v) => setFilter("locationId", v)
    },
    {
      key: "level",
      label: "Level",
      allLabel: "All levels",
      options: levelOptions,
      value: levelScope === "all" ? null : levelScope,
      onChange: (v) => setFilter("level", v || "all")
    }
  ];

  // Same cart hand-off as the Group Classes timetable (useClassBooking.handleJoin).
  const handleJoin = (event) => {
    addToCart("class", {
      type: "class",
      bookishId: event._id,
      name: event.name,
      date: event.date,
      time: event.time,
      duration: event.duration,
      trainerId: event.trainer?._id || null,
      trainerName: event.trainer?.name || null,
      facilityId: event.facility?._id || null,
      facilityName: event.facility?.name || null,
      serviceId: event.serviceId,
      pricingId: event.pricingId,
      price: event.price,
      currency,
      locationId: event.location?._id || null,
      locationName: event.location?.name || null
    });
    if (isAuthenticated) {
      navigate(`/${slug}/checkout`);
    } else {
      enqueueSnackbar("Please sign in to complete your booking", { variant: "warning" });
      navigate(`/${slug}/login?redirect=checkout`);
    }
  };

  const selectDate = (date) => setFilter("date", date);
  const showAllLevels = () => setFilter("level", "all");
  const openEvent = (event) => navigate(`/${slug}/classes/${event._id}`);
  const goHome = () => navigate(`/${slug}`);

  return {
    currency,
    categoryName,
    data,
    loading,
    error,
    filterDefs,
    totalEvents: events.length,
    hiddenByLevel,
    eventDates,
    selectedDate,
    selectDate,
    eventsByMonth,
    handleJoin,
    openEvent,
    showAllLevels,
    goBack,
    goHome
  };
}
